import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  DEFAULT_SLOT_NAMES,
  MAX_FM_DEPTH,
  clonePreset,
  normalizePreset,
  type FilterType,
  type InstrumentSlot,
  type ModRoute,
  type ModSource,
  type ModTarget,
  type SynthPreset,
  type Waveform,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import {
  categorySortIndex,
  PRESET_CATEGORIES,
} from "../lib/presetBank";
import { getLabActions } from "../labActions";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore } from "../store/uiStore";
import { formatOctaveLabel, MusicalKeyboard } from "./MusicalKeyboard";
import { ParamKnob } from "./ParamKnob";
import { SearchableSelect, type SearchableSelectOption } from "./SearchableSelect";
import { Checkbox } from "./ui/Checkbox";
import { instrumentPresetLearnPath } from "../controls/instrumentPresetLeaves";
import { LearnableControl } from "./LearnableControl";

const STORAGE_KEY = "audio-lab:synth-author-modal";
const PANEL_WIDTH = 980;
const PANEL_MIN_HEIGHT = 640;

const WAVES: Waveform[] = ["sine", "tri", "saw", "square", "noise"];
const FILTERS: FilterType[] = ["lowpass", "highpass", "bandpass"];
const LFO_WAVES: Array<"sine" | "tri"> = ["sine", "tri"];
const MOD_SOURCES: ModSource[] = ["ampEnv", "modEnv", "lfo", "key"];
const MOD_TARGETS: ModTarget[] = ["pitch", "cutoff", "amp", "pwm", "fmDepth", "amDepth"];
const FM_TARGETS = ["osc1", "osc2", "both"] as const;

type PanelPos = { left: number; top: number };

type KnobSpec = {
  path: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
};

type SelectSpec = {
  path: string;
  label: string;
  options: readonly string[];
};

type SectionSpec = {
  title: string;
  controls: Array<{ kind: "knob"; spec: KnobSpec } | { kind: "select"; spec: SelectSpec }>;
};

const SECTIONS: SectionSpec[] = [
  {
    title: "Osc 1",
    controls: [
      { kind: "select", spec: { path: "osc1.wave", label: "Wave", options: WAVES } },
      { kind: "knob", spec: { path: "osc1.octave", label: "Oct", min: -3, max: 3, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc1.semis", label: "Semi", min: -12, max: 12, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc1.detuneCents", label: "Detune", min: -100, max: 100, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc1.level", label: "Level", min: 0, max: 1, step: 0.01, defaultValue: 0.8 } },
      { kind: "knob", spec: { path: "osc1.pwm", label: "PWM", min: 0, max: 1, step: 0.01, defaultValue: 0.5 } },
    ],
  },
  {
    title: "Osc 2",
    controls: [
      { kind: "select", spec: { path: "osc2.wave", label: "Wave", options: WAVES } },
      { kind: "knob", spec: { path: "osc2.octave", label: "Oct", min: -3, max: 3, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc2.semis", label: "Semi", min: -12, max: 12, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc2.detuneCents", label: "Detune", min: -100, max: 100, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc2.level", label: "Level", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc2.pwm", label: "PWM", min: 0, max: 1, step: 0.01, defaultValue: 0.5 } },
    ],
  },
  {
    title: "Osc 3",
    controls: [
      { kind: "select", spec: { path: "osc3.wave", label: "Wave", options: WAVES } },
      { kind: "knob", spec: { path: "osc3.octave", label: "Oct", min: -3, max: 3, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc3.semis", label: "Semi", min: -12, max: 12, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc3.detuneCents", label: "Detune", min: -100, max: 100, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc3.level", label: "Level", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc3.pwm", label: "PWM", min: 0, max: 1, step: 0.01, defaultValue: 0.5 } },
    ],
  },
  {
    title: "FM / Noise",
    controls: [
      { kind: "select", spec: { path: "fm.target", label: "Target", options: FM_TARGETS } },
      { kind: "knob", spec: { path: "fm.fmDepth", label: "FM", min: 0, max: MAX_FM_DEPTH, step: 0.01, defaultValue: 0 } },
      { kind: "knob", spec: { path: "fm.amDepth", label: "Ring", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
      { kind: "knob", spec: { path: "fm.velocityToFm", label: "Vel→FM", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
      { kind: "knob", spec: { path: "noise.level", label: "Noise", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
      { kind: "knob", spec: { path: "glideMs", label: "Glide", min: 0, max: 500, step: 1, defaultValue: 0 } },
    ],
  },
  {
    title: "Filter",
    controls: [
      { kind: "select", spec: { path: "filter.type", label: "Type", options: FILTERS } },
      { kind: "knob", spec: { path: "filter.cutoffHz", label: "Cutoff", min: 40, max: 12000, step: 1, defaultValue: 4000 } },
      { kind: "knob", spec: { path: "filter.resonance", label: "Res", min: 0, max: 1, step: 0.01, defaultValue: 0.1 } },
    ],
  },
  {
    title: "Amp env",
    controls: [
      { kind: "knob", spec: { path: "ampEnv.a", label: "A", min: 0.001, max: 2, step: 0.001, defaultValue: 0.01 } },
      { kind: "knob", spec: { path: "ampEnv.d", label: "D", min: 0.001, max: 2, step: 0.001, defaultValue: 0.1 } },
      { kind: "knob", spec: { path: "ampEnv.s", label: "S", min: 0, max: 1, step: 0.01, defaultValue: 0.7 } },
      { kind: "knob", spec: { path: "ampEnv.r", label: "R", min: 0.001, max: 3, step: 0.001, defaultValue: 0.2 } },
    ],
  },
  {
    title: "Mod env",
    controls: [
      { kind: "knob", spec: { path: "modEnv.a", label: "A", min: 0.001, max: 2, step: 0.001, defaultValue: 0.01 } },
      { kind: "knob", spec: { path: "modEnv.d", label: "D", min: 0.001, max: 2, step: 0.001, defaultValue: 0.2 } },
      { kind: "knob", spec: { path: "modEnv.s", label: "S", min: 0, max: 1, step: 0.01, defaultValue: 0.2 } },
      { kind: "knob", spec: { path: "modEnv.r", label: "R", min: 0.001, max: 3, step: 0.001, defaultValue: 0.2 } },
    ],
  },
  {
    title: "LFO",
    controls: [
      { kind: "select", spec: { path: "lfo.wave", label: "Wave", options: LFO_WAVES } },
      { kind: "knob", spec: { path: "lfo.rateHz", label: "Rate", min: 0, max: 20, step: 0.1, defaultValue: 4 } },
      { kind: "knob", spec: { path: "lfo.depth", label: "Depth", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
    ],
  },
];

/**
 * Floating synth author: browser, knobs, mod matrix, keyboard.
 */
export function SynthAuthorModal() {
  const open = useStoreSelector(uiStore, s => s.authorOpen);
  const instrumentSlot = useStoreSelector(uiStore, s => s.instrumentSlot);
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const presets = useStoreSelector(projectStore, s => s.presets);
  const bank = useStoreSelector(projectStore, s => s.bank);

  const slot = (instrumentSlot ?? 0) as InstrumentSlot;
  const isSynth = instruments[slot]?.kind === "synth";
  const raw = presets[slot];
  const preset = raw ? normalizePreset(raw) : null;

  const panelRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const [pos, setPos] = useState<PanelPos>(() => loadPos() ?? { left: 12, top: 12 });
  const [octaveShift, setOctaveShift] = useState(0);
  const [dragging, setDragging] = useState(false);

  const bankId =
    bank.find(entry => entry.name === preset?.name)?.id
    ?? bank[0]?.id
    ?? "";

  const bankOptions = useMemo((): SearchableSelectOption[] => {
    const ordered = bank.slice().sort((a, b) => {
      const byCat = categorySortIndex(a.category) - categorySortIndex(b.category);
      if (byCat !== 0) {
        return byCat;
      }
      return a.name.localeCompare(b.name);
    });
    return ordered.map(item => ({
      value: item.id,
      label: item.name,
      group: item.category,
      hint: item.tags?.join(", "),
    }));
  }, [bank]);

  const categoryFilters = useMemo(
    () => PRESET_CATEGORIES.map(entry => ({ id: entry.id, label: entry.label })),
    [],
  );

  useEffect(() => {
    if (!open || !isSynth) {
      return;
    }
    setPos(prev => {
      const el = panelRef.current;
      const width = el?.offsetWidth || PANEL_WIDTH;
      const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(loadPos() ?? defaultPos(), width, height);
      savePos(next);
      return next.left === prev.left && next.top === prev.top ? prev : next;
    });
    panelRef.current?.focus();
  }, [open, isSynth]);

  useEffect(() => {
    if (!open || !isSynth) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
    function onResize(): void {
      setPos(prev => {
        const el = panelRef.current;
        const width = el?.offsetWidth || PANEL_WIDTH;
        const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
        const next = clampPos(prev, width, height);
        savePos(next);
        return next;
      });
    }
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onResize);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open, isSynth]);

  useLayoutEffect(() => {
    if (!open || !isSynth) {
      return;
    }
    setOctaveShift(0);
  }, [open, slot, isSynth]);

  function close(): void {
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    getLabActions()?.closeInstrumentAuthor?.();
    patchUi({ authorOpen: false });
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest("button")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);

    const onMove = (moveEvent: PointerEvent): void => {
      const width = el.offsetWidth || PANEL_WIDTH;
      const height = el.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(
        {
          left: moveEvent.clientX - offsetX,
          top: moveEvent.clientY - offsetY,
        },
        width,
        height,
      );
      savePos(next);
      setPos(next);
    };

    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  function commit(next: SynthPreset): void {
    getLabActions()?.updateInstrumentPreset?.(slot, next);
  }

  function patchPath(path: string, value: unknown): void {
    if (!preset) {
      return;
    }
    const next = clonePreset(preset);
    setPath(next as unknown as Record<string, unknown>, path, value);
    if (path === "glideMs" && typeof value === "number" && value <= 0) {
      delete next.glideMs;
    }
    commit(next);
  }

  function updateRoutes(routes: ModRoute[]): void {
    if (!preset) {
      return;
    }
    commit({ ...clonePreset(preset), routes });
  }

  if (!open || !isSynth) {
    return null;
  }

  const slotLabel = DEFAULT_SLOT_NAMES[slot] ?? `I${slot + 1}`;
  const title = preset
    ? `Synth author · ${preset.name} · ${slotLabel}`
    : "Synth author";

  return createPortal(
    <div
      ref={panelRef}
      className={`synth-author fixed z-[55] flex h-[min(760px,calc(100vh-24px))] w-[min(980px,calc(100vw-24px))] max-[720px]:h-[calc(100vh-16px)] max-[720px]:w-[calc(100vw-16px)] flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-xs text-slate-300 shadow-[0_28px_64px_rgba(0,0,0,0.5)] outline-none ${dragging ? "select-none" : ""}`}
      tabIndex={0}
      role="dialog"
      aria-modal="false"
      aria-label="Synth author"
      style={{ left: pos.left, top: pos.top }}
    >
      <div
        className="synth-author-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-white/8 bg-lab-panel-bar px-[0.7rem] py-2"
        onPointerDown={beginDrag}
      >
        <span className="synth-author-title min-w-0 truncate text-xs font-semibold text-slate-100">
          {title}
        </span>
        <button
          type="button"
          className="rounded border border-transparent bg-transparent px-2 py-0.5 text-xs text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-slate-200"
          onPointerDown={event => event.stopPropagation()}
          onClick={() => {
            close();
            getLabActions()?.setStatus?.("Synth author closed.");
          }}
        >
          Close
        </button>
      </div>

      <div className="synth-author-browser flex shrink-0 items-center gap-[0.45rem] border-b border-white/[0.06] px-[0.55rem] py-[0.45rem]">
        <SearchableSelect
          className="min-w-0 flex-1"
          ariaLabel="Preset browser"
          searchPlaceholder="Search name, category, or tag"
          emptyMessage="No presets match."
          value={bankId}
          options={bankOptions}
          filters={categoryFilters}
          onChange={id => getLabActions()?.applyInstrumentBankEntry?.(slot, id)}
        />
        <div className="flex shrink-0 gap-[0.3rem]">
          <button
            type="button"
            className="border border-lab-line bg-lab-panel-deep px-2 py-[0.28rem] text-[11px]"
            onClick={() => getLabActions()?.saveInstrumentBank?.(slot)}
          >
            Save
          </button>
          <button
            type="button"
            className="border border-lab-line bg-lab-panel-deep px-2 py-[0.28rem] text-[11px]"
            onClick={() => getLabActions()?.saveInstrumentBankAs?.(slot)}
          >
            Save as…
          </button>
        </div>
      </div>

      {preset ? (
        <div className="synth-author-scroll flex min-h-0 flex-1 flex-col gap-[0.65rem] overflow-auto bg-[radial-gradient(120%_80%_at_50%_0%,rgba(61,139,253,0.05),transparent_55%),var(--panel)] px-[0.65rem] pt-[0.55rem] pb-3">
          <label className="flex items-center gap-2 text-[11px] tracking-[0.04em] text-slate-500 uppercase">
            <span>Name</span>
            <input
              type="text"
              className="min-w-0 flex-1 rounded-md border border-white/12 bg-white/[0.04] px-2 py-[0.35rem] text-xs tracking-normal text-slate-200 normal-case [font:inherit]"
              value={preset.name}
              onChange={event => patchPath("name", event.target.value)}
            />
          </label>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-[0.55rem]">
            {SECTIONS.map(section => (
              <section
                key={section.title}
                className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]"
              >
                <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                  {section.title}
                </h3>
                <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                  {section.controls.map(control =>
                    control.kind === "knob" ? (
                      <AuthorKnob
                        key={control.spec.path}
                        slot={slot}
                        spec={control.spec}
                        value={numberAtPath(preset, control.spec.path, control.spec.defaultValue)}
                        onChange={v => patchPath(control.spec.path, v)}
                      />
                    ) : (
                      <AuthorSelect
                        key={control.spec.path}
                        spec={control.spec}
                        value={String(
                          getPath(preset, control.spec.path)
                            ?? control.spec.options[0]
                            ?? "",
                        )}
                        onChange={v => patchPath(control.spec.path, v)}
                      />
                    ),
                  )}
                </div>
              </section>
            ))}
          </div>

          <AuthorRoutes slot={slot} routes={preset.routes} onChange={updateRoutes} />
        </div>
      ) : (
        <div className="synth-author-scroll muted flex min-h-0 flex-1 flex-col overflow-auto px-[0.65rem] pt-[0.55rem] pb-3">
          No preset for this slot.
        </div>
      )}

      <div className="synth-author-keys shrink-0 border-t border-white/8 bg-lab-panel-bar px-[0.55rem] pt-1.5 pb-2">
        <div className="mb-[0.3rem] flex items-center justify-between gap-2 text-[11px] text-lab-muted">
          <span>Keyboard</span>
          <span className="muted">{formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={48}
          octaves={3}
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
        <p className="muted mt-[0.35rem] mb-0 text-[11px]">
          A–' white · W E T Y U O P black · Z/X octave
        </p>
      </div>
    </div>,
    document.body,
  );
}

function AuthorKnob({
  slot,
  spec,
  value,
  onChange,
}: {
  slot: InstrumentSlot;
  spec: KnobSpec;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <ParamKnob
      label={spec.label}
      min={spec.min}
      max={spec.max}
      step={spec.step}
      value={value}
      defaultValue={spec.defaultValue}
      density="compact"
      size={44}
      format={v => formatPathValue(spec.path, v)}
      learnPath={instrumentPresetLearnPath(slot, spec.path)}
      onChange={onChange}
    />
  );
}

function AuthorSelect({
  spec,
  value,
  onChange,
}: {
  spec: SelectSpec;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-[4.2rem] flex-col items-center gap-[0.28rem]">
      <span className="text-center text-[10px] leading-tight tracking-[0.04em] text-slate-500 uppercase">
        {spec.label}
      </span>
      <select
        className="h-8 w-[4.6rem] rounded-md border border-white/12 bg-white/[0.04] px-1 text-[11px] text-slate-200 [font:inherit]"
        value={value}
        onChange={event => onChange(event.target.value)}
      >
        {spec.options.map(opt => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function AuthorRoutes({
  slot,
  routes,
  onChange,
}: {
  slot: InstrumentSlot;
  routes: ModRoute[];
  onChange: (routes: ModRoute[]) => void;
}) {
  return (
    <div className="synth-author-routes rounded-lg border border-white/[0.07] bg-black/14 px-[0.55rem] pt-[0.45rem] pb-[0.55rem]">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="m-0 text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
          Mod matrix
        </h3>
        <button
          type="button"
          className="px-[0.45rem] py-0.5 text-[11px]"
          onClick={() =>
            onChange([...routes, { source: "modEnv", target: "cutoff", depth: 1 }])
          }
        >
          + Route
        </button>
      </div>
      <div className="flex flex-col gap-[0.3rem]">
        {routes.map((route, index) => (
          <div key={index} className="flex flex-wrap items-center gap-[0.35rem]">
            <select
              className={authorRouteControl}
              value={route.source}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, source: event.target.value as ModSource } : r,
                );
                onChange(next);
              }}
            >
              {MOD_SOURCES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              className={authorRouteControl}
              value={route.target}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, target: event.target.value as ModTarget } : r,
                );
                onChange(next);
              }}
            >
              {MOD_TARGETS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <LearnableControl
              path={instrumentPresetLearnPath(slot, `routes.${index}.depth`)}
              className="inline-flex"
            >
              <input
                type="number"
                step="0.1"
                title="Depth"
                className={`${authorRouteControl} w-14`}
                value={route.depth}
                onChange={event => {
                  const depth = Number(event.target.value) || 0;
                  const next = routes.map((r, i) => (i === index ? { ...r, depth } : r));
                  onChange(next);
                }}
              />
            </LearnableControl>
            <label className="flex items-center gap-[0.28rem] text-[10px] text-lab-muted">
              <Checkbox
                checked={Boolean(route.invert)}
                aria-label={`Route ${index + 1} invert`}
                onCheckedChange={invert => {
                  const next = routes.map((r, i) =>
                    i === index ? { ...r, invert: invert || undefined } : r,
                  );
                  onChange(next);
                }}
              />
              inv
            </label>
            <button
              type="button"
              title="Remove route"
              onClick={() => onChange(routes.filter((_, i) => i !== index))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const authorRouteControl =
  "h-7 rounded border border-white/12 bg-white/[0.04] px-[0.3rem] text-[11px] text-slate-200 [font:inherit]";

function getPath(preset: SynthPreset, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = preset;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const next = cur[key];
    if (next == null || typeof next !== "object") {
      cur[key] = {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

function numberAtPath(preset: SynthPreset, path: string, fallback: number): number {
  let value = getPath(preset, path);
  if (path === "glideMs" && value === undefined) {
    value = 0;
  }
  if (path.endsWith(".pwm") && value === undefined) {
    value = 0.5;
  }
  return typeof value === "number" ? value : fallback;
}

function formatPathValue(path: string, value: number): string {
  if (
    path.includes("Hz")
    || path === "glideMs"
    || path.includes("detune")
    || path.includes("octave")
    || path.includes("semis")
  ) {
    return String(Math.round(value * 1000) / 1000);
  }
  if (path.includes("Env") || path.endsWith(".a") || path.endsWith(".d") || path.endsWith(".r")) {
    return value.toFixed(3);
  }
  return value.toFixed(2);
}

function loadPos(): PanelPos | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<PanelPos>;
    if (typeof parsed.left === "number" && typeof parsed.top === "number") {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    // ignore
  }
  return undefined;
}

function savePos(pos: PanelPos): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function defaultPos(): PanelPos {
  const left = Math.max(12, (window.innerWidth - PANEL_WIDTH) / 2);
  const top = Math.max(12, Math.min(48, window.innerHeight * 0.06));
  return { left, top };
}

function clampPos(pos: PanelPos, width: number, height: number): PanelPos {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(
    8,
    window.innerHeight - Math.min(height, window.innerHeight - 16) - 8,
  );
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}
